import { createFileRoute, Link } from "@tanstack/react-router";
import { Star, Quote, CalendarCheck, Phone } from "lucide-react";
import { BUSINESS } from "@/lib/contact";

export const Route = createFileRoute("/reviews")({
  component: ReviewsPage,
  head: () => ({
    meta: [
      { title: "Customer Reviews | StillFusion Appliance Repair Sacramento" },
      {
        name: "description",
        content:
          "Real reviews from Sacramento-area homeowners. See why locals trust StillFusion for fast, honest, 5-star appliance repair.",
      },
      { property: "og:title", content: "Customer Reviews | StillFusion" },
      { property: "og:description", content: "5-star service across the Sacramento area." },
      { property: "og:url", content: "https://stillfusionappliance.com/reviews" },
    ],
    links: [
      { rel: "canonical", href: "https://stillfusionappliance.com/reviews" },
    ],
  }),
});

const reviews = [
  {
    text: "My fridge stopped cooling on a Friday night. StillFusion was here Saturday morning and had it fixed in an hour. Lifesavers!",
    name: "Sarah J.",
    location: "Sacramento, CA",
    appliance: "Refrigerator",
  },
  {
    text: "Honest pricing and very professional. The tech explained exactly what was wrong with my dryer before starting. No upsell, no surprises.",
    name: "Mike T.",
    location: "Roseville, CA",
    appliance: "Dryer",
  },
  {
    text: "I've used them twice now (oven and washer). Always on time, clean up after themselves, and the repairs hold up.",
    name: "Elena R.",
    location: "Folsom, CA",
    appliance: "Oven & Washer",
  },
  {
    text: "Booked by text in the morning, oven was working by dinner. Friendly tech, explained everything clearly.",
    name: "Anna P.",
    location: "Folsom, CA",
    appliance: "Oven",
  },
  {
    text: "Dishwasher wouldn't drain. Diagnosed in 10 minutes, fixed within the hour. Fair price and very polite.",
    name: "David L.",
    location: "Elk Grove, CA",
    appliance: "Dishwasher",
  },
  {
    text: "Came out same day for our leaking washer. Had the part on the truck. So much easier than dealing with the big-name companies.",
    name: "James R.",
    location: "Antelope, CA",
    appliance: "Washer",
  },
];

const stats = [
  { value: "5.0★", label: "Average rating" },
  { value: "1000+", label: "Repairs completed" },
  { value: "98%", label: "Same-day fixes" },
  { value: "100%", label: "Local & insured" },
];

function ReviewsPage() {
  return (
    <>
      {/* Hero */}
      <section className="border-b border-border bg-secondary/40">
        <div className="mx-auto max-w-6xl px-5 py-16 sm:py-20">
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-primary">
              Reviews
            </span>
            <h1 className="mt-4 text-4xl font-bold tracking-tight sm:text-5xl">
              Trusted by hundreds of Sacramento homeowners
            </h1>
            <p className="mt-4 inline-flex items-center gap-2 text-lg text-muted-foreground">
              <span className="inline-flex items-center gap-1 text-accent">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star key={i} className="h-5 w-5 fill-current" />
                ))}
              </span>
              5.0 average rating across the Sacramento area.
            </p>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="mx-auto max-w-6xl px-5 py-12">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {stats.map((s) => (
            <div key={s.label} className="surface-card p-6 text-center">
              <div className="text-3xl font-bold text-primary">{s.value}</div>
              <div className="mt-1 text-sm text-muted-foreground">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Review grid */}
      <section className="mx-auto max-w-6xl px-5 pb-16">
        <div className="text-center">
          <span className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-primary">
            What clients say
          </span>
          <h2 className="mt-3 text-3xl font-bold tracking-tight sm:text-4xl">
            Real reviews from real neighbors
          </h2>
          <p className="mx-auto mt-3 max-w-2xl text-muted-foreground">
            A few words from homeowners across the Sacramento area.
          </p>
        </div>
        <div className="mt-12 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {reviews.map((r) => (
            <article key={r.name + r.text.slice(0, 12)} className="surface-card flex flex-col p-6">
              <Quote className="h-6 w-6 text-primary/30" />
              <p className="mt-3 flex-1 text-sm leading-relaxed text-foreground/90">
                "{r.text}"
              </p>
              <div className="mt-5 flex items-center gap-1 text-accent">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star key={i} className="h-4 w-4 fill-current" />
                ))}
              </div>
              <div className="mt-4 flex items-center justify-between border-t border-border pt-4 text-xs">
                <span>
                  <h2 className="text-sm font-semibold text-foreground">{r.name}</h2>
                  <span className="text-muted-foreground">{r.location}</span>
                </span>
                <span className="rounded-full bg-primary/10 px-3 py-1 font-semibold text-primary">
                  {r.appliance}
                </span>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="mx-auto max-w-6xl px-5 pb-20">
        <div className="surface-elevated bg-hero-gradient p-10 text-center text-white sm:p-14">
          <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">
            Want the same experience?
          </h2>
          <p className="mx-auto mt-3 max-w-xl text-white/80">
            Book online or call us directly. Most appointments scheduled same or next day.
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Link to="/contact" className="btn-accent inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-semibold">
              <CalendarCheck className="h-4 w-4" /> Book Service
            </Link>
            <a href={`tel:${BUSINESS.phone}`} className="inline-flex items-center gap-2 rounded-full border border-white/30 bg-white/10 px-6 py-3 text-sm font-semibold backdrop-blur transition hover:bg-white/15">
              <Phone className="h-4 w-4" /> Call Now
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
